import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, MailCheck, MailWarning } from "lucide-react";
import { errorMessage, ApiError, isPlaceholderEmail, type EmailStatus } from "@atlas/shared";
import { useAuth } from "../hooks/useAuth";
import { resendVerification } from "../api/auth";
import { EMAIL_STATUS_QUERY_KEY, fetchEmailStatus, requestEmailChange } from "../api/account";
import { fetchOnboarding, ONBOARDING_QUERY_KEY } from "../api/onboarding";
import { Button } from "./Button";

// ─── "Add your email" / "Verify your email" card ─────────────────────────────
// Shown wherever the account cannot be emailed: Strava-only signups carry a
// placeholder address (`@athlete.atlas.internal`) and a freshly signed-up
// address is unverified until the link is clicked. Not dismissable - it
// disappears when the address is real and verified. Rendered on the
// dashboard (above the onboarding card), the profile page and the plan page
// after a `verify_email` 403. Mobile port: src/components/EmailCaptureCard.tsx.

export const EMAIL_CAPTURE_ANCHOR_ID = "email-capture";

export function scrollToEmailCapture(): void {
  if (typeof document === "undefined") return;
  const el = document.getElementById(EMAIL_CAPTURE_ANCHOR_ID);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  const input = el.querySelector<HTMLInputElement>("input[type=email]");
  input?.focus({ preventScroll: true });
}

/** True when the card would render for this user (placeholder or unverified). */
export function needsEmailCapture(user: { email: string; emailVerified: boolean } | null | undefined): boolean {
  if (!user) return false;
  return isPlaceholderEmail(user.email) || !user.emailVerified;
}

function useCountdown(until: string | null | undefined): number {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!until) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [until]);
  if (!until) return 0;
  return Math.max(0, Math.ceil((new Date(until).getTime() - now) / 1000));
}

const inputStyle: React.CSSProperties = {
  flex: "1 1 220px",
  minWidth: 0,
  height: "var(--control-md)",
  padding: "0 12px",
  borderRadius: "var(--radius-md)",
  border: "1px solid var(--border-strong)",
  background: "var(--surface-sunken)",
  color: "var(--text-primary)",
  fontFamily: "var(--font-sans)",
  fontSize: 14,
};

export function EmailCaptureCard({
  compact = false,
  deferToChecklist = false,
}: {
  compact?: boolean;
  /**
   * Dashboard only: while the getting-started strip (OnboardingCard) is up it
   * carries the verify-email step itself, so the unverified version of this
   * card stays hidden. Placeholder (Strava-only) addresses still show.
   */
  deferToChecklist?: boolean;
}) {
  const { user } = useAuth();
  const onboardingQuery = useQuery({ queryKey: ONBOARDING_QUERY_KEY, queryFn: fetchOnboarding, enabled: false });
  const checklistUp = deferToChecklist && !!onboardingQuery.data && !onboardingQuery.data.completedAt;
  const queryClient = useQueryClient();
  const placeholder = user ? isPlaceholderEmail(user.email) : false;
  const show = needsEmailCapture(user);

  const statusQuery = useQuery({
    queryKey: EMAIL_STATUS_QUERY_KEY,
    queryFn: fetchEmailStatus,
    enabled: show,
    staleTime: 15_000,
    // The link is clicked in another tab or on the phone; pick the change up
    // without a reload.
    refetchInterval: show ? 30_000 : false,
  });
  const status: EmailStatus | undefined = statusQuery.data;

  React.useEffect(() => {
    if (status && status.verified && !status.placeholder && show) {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      void queryClient.invalidateQueries({ queryKey: ONBOARDING_QUERY_KEY });
    }
  }, [status, show, queryClient]);

  const [changing, setChanging] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [localPending, setLocalPending] = React.useState<EmailStatus["pending"]>(null);
  const [resent, setResent] = React.useState(false);

  const pending = status?.pending ?? localPending;
  const cooldown = useCountdown(pending?.resendAvailableAt);

  const changeMutation = useMutation({
    mutationFn: requestEmailChange,
    onSuccess: (res) => {
      setLocalPending(res.pending);
      setChanging(false);
      setPassword("");
      queryClient.setQueryData<EmailStatus>(EMAIL_STATUS_QUERY_KEY, (prev) =>
        prev ? { ...prev, pending: res.pending } : prev,
      );
    },
  });

  const resendMutation = useMutation({
    mutationFn: resendVerification,
    onSuccess: () => setResent(true),
  });

  if (!user || !show) return null;
  if (checklistUp && !placeholder) return null;

  const requiresPassword = status?.requiresPassword ?? user.hasPassword;

  const showForm = placeholder ? !pending || changing : changing;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    changeMutation.mutate({ email: trimmed, ...(requiresPassword ? { password } : {}) });
  }

  let title: string;
  let lead: string;
  if (pending && !changing) {
    title = placeholder ? "Check your inbox" : "Confirm your new address";
    lead = `We sent a link to ${pending.email}. Click it and you're done. Not there? Check spam, and note that an address already registered to another Atlas account won't receive it.`;
  } else if (placeholder) {
    title = "Add an email so we can reach you";
    lead = "You signed up with Strava, so Atlas has no way to contact you yet. An email is needed for password resets, receipts and your monthly recap.";
  } else {
    title = "Verify your email";
    lead = `We sent a verification link to ${user.email} when you signed up. Password resets, receipts and your monthly recap wait until it's confirmed.`;
  }

  const Icon = pending && !changing ? MailCheck : placeholder ? Mail : MailWarning;

  return (
    <section
      id={EMAIL_CAPTURE_ANCHOR_ID}
      aria-labelledby={`${EMAIL_CAPTURE_ANCHOR_ID}-title`}
      style={{
        background: "var(--surface-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--shadow-sm), var(--ring-top)",
        padding: compact ? "14px 16px" : "18px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        scrollMarginTop: 16,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <span
          aria-hidden
          style={{
            width: 36,
            height: 36,
            borderRadius: "var(--radius-md)",
            background: "var(--accent-soft)",
            border: "1px solid var(--border-gold)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--text-accent)",
            flexShrink: 0,
          }}
        >
          <Icon size={18} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="eyebrow" style={{ fontSize: 10, marginBottom: 4 }}>
            {placeholder ? "No email on file" : "Email not verified"}
          </div>
          <h2 id={`${EMAIL_CAPTURE_ANCHOR_ID}-title`} style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 600, color: "var(--text-primary)", margin: 0 }}>
            {title}
          </h2>
          <p style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5, margin: "4px 0 0" }}>{lead}</p>
        </div>
      </div>

      {showForm ? (
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div className="r-wrap" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              required
              style={inputStyle}
              aria-label="Email address"
            />
            {requiresPassword && (
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Current password"
                autoComplete="current-password"
                required
                style={inputStyle}
                aria-label="Current password"
              />
            )}
            <Button
              type="submit"
              variant="primary"
              size="md"
              disabled={changeMutation.isPending || !email.trim() || (requiresPassword && !password)}
            >
              {changeMutation.isPending ? "Sending…" : "Send confirmation link"}
            </Button>
            {changing && (
              <Button type="button" variant="ghost" size="md" onClick={() => setChanging(false)}>
                Cancel
              </Button>
            )}
          </div>
          {requiresPassword && !changing && (
            <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>
              Your password confirms it's you making the change.
            </p>
          )}
          {changeMutation.isError && (
            <p role="alert" style={{ fontSize: 12, color: "var(--status-danger)", margin: 0 }}>
              {errorMessage(changeMutation.error, "Couldn't send the link. Try again.")}
            </p>
          )}
        </form>
      ) : pending ? (
        <div className="r-wrap" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Button
            variant="secondary"
            size="sm"
            disabled={cooldown > 0 || changeMutation.isPending}
            onClick={() => changeMutation.mutate({ email: pending.email })}
          >
            {changeMutation.isPending ? "Sending…" : cooldown > 0 ? `Resend in ${cooldown}s` : "Resend link"}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => { setEmail(""); setChanging(true); }}>
            Use a different address
          </Button>
          {changeMutation.isError && (
            <span role="alert" style={{ fontSize: 12, color: "var(--status-danger)" }}>
              {errorMessage(changeMutation.error, "Couldn't resend. Try again.")}
            </span>
          )}
        </div>
      ) : (
        <div className="r-wrap" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Button
            variant="primary"
            size="sm"
            disabled={resendMutation.isPending || resent}
            onClick={() => resendMutation.mutate()}
          >
            {resendMutation.isPending ? "Sending…" : resent ? "Link sent" : "Resend verification"}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => { setEmail(""); setChanging(true); }}>
            Wrong address? Change it
          </Button>
          {resent && (
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Check your inbox and spam folder.</span>
          )}
          {resendMutation.isError && (
            <span role="alert" style={{ fontSize: 12, color: "var(--status-danger)" }}>
              {errorMessage(resendMutation.error, "Couldn't resend. Try again.")}
            </span>
          )}
        </div>
      )}
    </section>
  );
}
